import { useTranslation } from 'react-i18next';
import { formatGermanDate } from 'utils/dates';
import { formatBoolean, optionalValue } from 'utils/formatters';
import { HorizontalSeparator } from '../HorizontalSeparator/HorizontalSeparator';
import { HealthQuestionsProps } from 'pages/DigitalOrderPage/models/insuredPersonDetailModels';
import Typography from '@mui/material/Typography';

export const HealthQuestions = ({ questions }: HealthQuestionsProps) => {
  const { t } = useTranslation('view360', { keyPrefix: 'insuredPerson.health' });

  return (
    <div role="region" aria-label="Health Questions">
      {(questions || []).map((question, questionIndex) => {
        const questionId = `health-question-${questionIndex}`;
        const detailsId = `health-details-${questionIndex}`;

        const renderQuestion = () => (
          <div
            className="grid grid-cols-2 gap-[8px] my-s"
            role="table"
            aria-label={`Health question ${questionIndex + 1}`}
            tabIndex={0}
          >
            <div className="col-span-1" role="rowheader">
              <Typography
                variant="bodySMBold"
                tabIndex={0}
                onFocus={(e) => {
                  e.currentTarget.setAttribute('aria-selected', 'true');
                }}
                onBlur={(e) => {
                  e.currentTarget.removeAttribute('aria-selected');
                }}
              >
                {question.text}
              </Typography>
            </div>
            <div className="col-span-1" role="cell">
              <Typography
                variant="bodySMRegular"
                tabIndex={0}
                onFocus={(e) => {
                  e.currentTarget.setAttribute('aria-selected', 'true');
                }}
                onBlur={(e) => {
                  e.currentTarget.removeAttribute('aria-selected');
                }}
              >
                {question.answer}
              </Typography>
            </div>
          </div>
        );

        const renderDetails = () => (
          <div role="region" aria-labelledby={questionId}>
            {(question.details || []).map((detail, detailIndex) => {
              const detailId = `${detailsId}-${detailIndex}`;

              return (
                <div
                  className="my-m p-m bg-surface-60"
                  key={detail.diagnosis}
                  role="table"
                  aria-label={`Health question ${questionIndex + 1} detail ${detailIndex + 1}`}
                  tabIndex={0}
                >
                  <div className="grid grid-cols-2 gap-[8px]">
                    <div className="col-span-2" role="rowheader">
                      <div className="col">
                        <Typography
                          variant="bodySMBold"
                          id={detailId}
                          tabIndex={0}
                          onFocus={(e) => {
                            e.currentTarget.setAttribute('aria-selected', 'true');
                          }}
                          onBlur={(e) => {
                            e.currentTarget.removeAttribute('aria-selected');
                          }}
                        >
                          {`${t('questions.detail')} ${detailIndex + 1}`}
                        </Typography>
                      </div>
                    </div>
                    <div className="col-span-2">
                      <div className="grid grid-cols-2 gap-[8px]">
                        <div className="col-span-1" role="rowheader">
                          <Typography
                            variant="bodySMBold"
                            tabIndex={0}
                            onFocus={(e) => {
                              e.currentTarget.setAttribute('aria-selected', 'true');
                            }}
                            onBlur={(e) => {
                              e.currentTarget.removeAttribute('aria-selected');
                            }}
                          >
                            {t('questions.attributes.diagnose')}
                          </Typography>
                        </div>
                        <div className="col-span-1" role="cell">
                          <Typography
                            variant="bodySMRegular"
                            tabIndex={0}
                            onFocus={(e) => {
                              e.currentTarget.setAttribute('aria-selected', 'true');
                            }}
                            onBlur={(e) => {
                              e.currentTarget.removeAttribute('aria-selected');
                            }}
                          >
                            {detail.diagnosis}
                          </Typography>
                        </div>
                      </div>
                    </div>
                    <div className="col-span-2">
                      <div className="grid grid-cols-2 gap-[8px]">
                        <div className="col-span-1" role="rowheader">
                          <Typography
                            variant="bodySMBold"
                            tabIndex={0}
                            onFocus={(e) => {
                              e.currentTarget.setAttribute('aria-selected', 'true');
                            }}
                            onBlur={(e) => {
                              e.currentTarget.removeAttribute('aria-selected');
                            }}
                          >
                            {t('questions.attributes.treatmentType')}
                          </Typography>
                        </div>
                        <div className="col-span-1" role="cell">
                          <Typography
                            variant="bodySMRegular"
                            tabIndex={0}
                            onFocus={(e) => {
                              e.currentTarget.setAttribute('aria-selected', 'true');
                            }}
                            onBlur={(e) => {
                              e.currentTarget.removeAttribute('aria-selected');
                            }}
                          >
                            {optionalValue(detail.treatmentType)}
                          </Typography>
                        </div>
                      </div>
                    </div>
                    <div className="col-span-2">
                      <div className="grid grid-cols-2 gap-[8px]">
                        <div className="col-span-1" role="rowheader">
                          <Typography
                            variant="bodySMBold"
                            tabIndex={0}
                            onFocus={(e) => {
                              e.currentTarget.setAttribute('aria-selected', 'true');
                            }}
                            onBlur={(e) => {
                              e.currentTarget.removeAttribute('aria-selected');
                            }}
                          >
                            {t('questions.attributes.treatmentDuration')}
                          </Typography>
                        </div>
                        <div className="col-span-1" role="cell">
                          <Typography
                            variant="bodySMRegular"
                            tabIndex={0}
                            onFocus={(e) => {
                              e.currentTarget.setAttribute('aria-selected', 'true');
                            }}
                            onBlur={(e) => {
                              e.currentTarget.removeAttribute('aria-selected');
                            }}
                          >
                            {`${optionalValue(
                              detail.treatmentStart,
                              formatGermanDate
                            )} - ${optionalValue(detail.treatmentEnd, formatGermanDate)}`}
                          </Typography>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-[8px]">
                    <div className="grid grid-cols-2 gap-[8px]">
                      <div className="col-span-1" role="rowheader">
                        <Typography
                          variant="bodySMBold"
                          tabIndex={0}
                          onFocus={(e) => {
                            e.currentTarget.setAttribute('aria-selected', 'true');
                          }}
                          onBlur={(e) => {
                            e.currentTarget.removeAttribute('aria-selected');
                          }}
                        >
                          {t('questions.attributes.operation')}
                        </Typography>
                      </div>
                      <div className="col-span-1" role="cell">
                        <Typography
                          variant="bodySMRegular"
                          tabIndex={0}
                          onFocus={(e) => {
                            e.currentTarget.setAttribute('aria-selected', 'true');
                          }}
                          onBlur={(e) => {
                            e.currentTarget.removeAttribute('aria-selected');
                          }}
                        >
                          {optionalValue(detail.hasOperation, formatBoolean)}
                        </Typography>
                      </div>
                    </div>
                    <div className="col-span-2">
                      <div className="grid grid-cols-2 gap-[8px]">
                        <div className="col-span-1" role="rowheader">
                          <Typography
                            variant="bodySMBold"
                            tabIndex={0}
                            onFocus={(e) => {
                              e.currentTarget.setAttribute('aria-selected', 'true');
                            }}
                            onBlur={(e) => {
                              e.currentTarget.removeAttribute('aria-selected');
                            }}
                          >
                            {t('questions.attributes.consenquences')}
                          </Typography>
                        </div>
                        <div className="col-span-1" role="cell">
                          <Typography
                            variant="bodySMRegular"
                            tabIndex={0}
                            onFocus={(e) => {
                              e.currentTarget.setAttribute('aria-selected', 'true');
                            }}
                            onBlur={(e) => {
                              e.currentTarget.removeAttribute('aria-selected');
                            }}
                          >
                            {optionalValue(detail.hasConsenquences, formatBoolean)}
                          </Typography>
                        </div>
                      </div>
                    </div>
                    <div className="col-span-2">
                      <div className="grid grid-cols-2 gap-[8px]">
                        <div className="col-span-1" role="rowheader">
                          <Typography
                            variant="bodySMBold"
                            tabIndex={0}
                            onFocus={(e) => {
                              e.currentTarget.setAttribute('aria-selected', 'true');
                            }}
                            onBlur={(e) => {
                              e.currentTarget.removeAttribute('aria-selected');
                            }}
                          >
                            {t('questions.attributes.consenquencesDate')}
                          </Typography>
                        </div>
                        <div className="col-span-1" role="cell">
                          <Typography
                            variant="bodySMRegular"
                            tabIndex={0}
                            onFocus={(e) => {
                              e.currentTarget.setAttribute('aria-selected', 'true');
                            }}
                            onBlur={(e) => {
                              e.currentTarget.removeAttribute('aria-selected');
                            }}
                          >
                            {optionalValue(detail.symptomsFreeFrom, formatGermanDate)}
                          </Typography>
                        </div>
                      </div>
                    </div>
                    <div className="col-span-2 grid grid-cols-2 gap-[8px]">
                      <Typography
                        variant="bodySMBold"
                        className="col-span-1"
                        role="rowheader"
                        tabIndex={0}
                        onFocus={(e) => {
                          e.currentTarget.setAttribute('aria-selected', 'true');
                        }}
                        onBlur={(e) => {
                          e.currentTarget.removeAttribute('aria-selected');
                        }}
                      >
                        {t('questions.attributes.operation')}
                      </Typography>
                      <Typography
                        variant="bodySMRegular"
                        className="col-span-1"
                        role="cell"
                        tabIndex={0}
                        onFocus={(e) => {
                          e.currentTarget.setAttribute('aria-selected', 'true');
                        }}
                        onBlur={(e) => {
                          e.currentTarget.removeAttribute('aria-selected');
                        }}
                      >
                        {optionalValue(detail.hasOperation, formatBoolean)}
                      </Typography>
                    </div>
                    <div className="col-span-2 grid grid-cols-2 gap-[8px]">
                      <Typography
                        variant="bodySMBold"
                        className="col-span-1"
                        role="rowheader"
                        tabIndex={0}
                        onFocus={(e) => {
                          e.currentTarget.setAttribute('aria-selected', 'true');
                        }}
                        onBlur={(e) => {
                          e.currentTarget.removeAttribute('aria-selected');
                        }}
                      >
                        {t('questions.attributes.operationDate')}
                      </Typography>
                      <Typography
                        variant="bodySMRegular"
                        className="col-span-1"
                        role="cell"
                        tabIndex={0}
                        onFocus={(e) => {
                          e.currentTarget.setAttribute('aria-selected', 'true');
                        }}
                        onBlur={(e) => {
                          e.currentTarget.removeAttribute('aria-selected');
                        }}
                      >
                        {optionalValue(detail.operationDate, formatGermanDate)}
                      </Typography>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        );

        return (
          <div key={question.id} id={questionId}>
            {renderQuestion()}
            {renderDetails()}
            <HorizontalSeparator className="my-[16px]" />
          </div>
        );
      })}
    </div>
  );
};
